/* eslint-disable no-unsafe-optional-chaining */
import { styled } from "@mui/material";
import { Editor } from "@tiptap/react";
import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import ColorGroup from "./ColorGroup";
import HighlightGroup from "./HighlightGroup";
import ImageGroup from "./ImageGroup";
import LinkGroup from "./LinkGroup";
import MaterialLinkGroup from "./MaterialLinkGroup";
import { SVMenuContext, SVMenuContextType } from "./sv-menu-context";
import TableGroup from "./TableGroup";
import { TextGroup } from "./text-group";
import TextAlignGroup from "./TextAlignGroup";
import TextDecorationGroup from "./TextDecorationGroup";

const MenuLengthWithMaterialLink = 610;
const MenuLengthWithoutMaterialLink = 520;

type SVMenuComponentWrapperProps = {
  isBubbleMenu: boolean;
  menuLength: number;
};

const SVMenuComponentWrapper = styled("div")<SVMenuComponentWrapperProps>`
  display: flex;
  align-items: center;
  box-shadow: ${({ isBubbleMenu }) =>
    isBubbleMenu ? "0px 0px 6px rgba(102, 102, 102, 0.25)" : "none"};
  padding: ${({ theme, isBubbleMenu }) =>
    isBubbleMenu ? `${theme.spacing(0.5)}` : "4px"};
  background-color: white;
  border-radius: "8px";
  width: ${({ isBubbleMenu, menuLength }) =>
    isBubbleMenu ? `${menuLength}px` : "100%"};
  height: ${({ isBubbleMenu }) => (isBubbleMenu ? "100%" : "auto !important")};
`;

type Props = {
  editor: Editor;
  isBubbleMenu?: boolean;
  isVisible?: boolean;
};

export const SVMenu = ({
  editor,
  isBubbleMenu = false,
  isVisible = true,
}: Props) => {
  const location = useLocation();
  const [subMenuOpen, setSubMenuOpen] = useState<Record<string, boolean>>({
    textGroup: false,
    textAlignGroup: false,
    colorGroup: false,
    linkGroup: false,
    highlightGroup: false,
    materialLinkGroup: false,
  });

  const turnOnMaterialLink: boolean = useMemo(() => {
    return (
      location.pathname.includes("/materials") || location.pathname === "/"
    );
  }, [location.pathname]);
  const menuLength = useMemo(
    () =>
      turnOnMaterialLink
        ? MenuLengthWithMaterialLink
        : MenuLengthWithoutMaterialLink,
    [turnOnMaterialLink],
  );

  const isOtherSubMenuOpen = useCallback(
    (myMenu: string) => {
      const openMenu = Object.keys(subMenuOpen).filter(
        (subMenu) => subMenuOpen[subMenu],
      );

      if (openMenu.length === 0) {
        return false;
      }
      return !openMenu.includes(myMenu);
    },
    [subMenuOpen],
  );

  const isSubMenuOpen = useCallback(
    () =>
      Object.keys(subMenuOpen).filter((subMenu) => subMenuOpen[subMenu])
        .length !== 0,
    [subMenuOpen],
  );

  const providerValues: SVMenuContextType = useMemo(() => {
    return {
      editor,
      subMenuOpen,
      setSubMenuOpen,
      isSubMenuOpen,
      isOtherSubMenuOpen,
    };
  }, [editor, isOtherSubMenuOpen, isSubMenuOpen, subMenuOpen]);

  return (
    <>
      {isVisible && (
        <SVMenuContext.Provider value={providerValues}>
          <SVMenuComponentWrapper
            menuLength={menuLength}
            isBubbleMenu={isBubbleMenu}
            onClick={(e) => {
              // Prevent fixed menu to close when clicking on the screen.
              e.stopPropagation();
            }}
          >
            <TextGroup />
            <TextAlignGroup />
            <ColorGroup />
            <HighlightGroup />
            <TextDecorationGroup />
            <LinkGroup />
            {turnOnMaterialLink && <MaterialLinkGroup />}
            <ImageGroup />
            <TableGroup />
          </SVMenuComponentWrapper>
        </SVMenuContext.Provider>
      )}
    </>
  );
};
